import { Log } from 'bblog';
import { XMLParser } from 'fast-xml-parser';
import { makeObservable, observable } from 'mobx';
import { type ApprovableInsightDrawable, CollectionModelDrawable } from './drawable';
import { CollectionModelDrawableState } from './drawable.state';

export type maskDetectionClass = 'pole' | 'transformer' | 'streetlight' | 'wire' | 'downguy' | 'spanguy';

export interface MaskSvgDrawableNode {
	id: string;
	class: string;
	// TODO: handle case where there are multiple of one kind of shape in a group
	polyline?: MaskDrawablePolyline;
	path?: MaskDrawablePath;
	rect?: MaskDrawableRect;
}

export interface MaskDrawablePolyline {
	id: string;
	class: string;
	points: string;
}

export interface MaskDrawablePath {
	id: string;
	class: string;
	d: string;
}

export interface MaskDrawableRect {
	id: string;
	class: string;
	height: string;
	width: string;
	x: string;
	y: string;
}

const parser = new XMLParser({
	attributeNamePrefix: '',
	ignoreAttributes: false,
	parseAttributeValue: true,
	trimValues: true,
	// all groups should be parsed as arrays even if there is only one child
	isArray: (name) => {
		if (name === 'g') {
			return true;
		}
		return false;
	},
});

export interface ParsedMaskSvg {
	svg?: {
		//group that contains all object type groups
		g: {
			id: maskDetectionClass;
			//group that contains all objects of the same type
			g: MaskSvgDrawableNode[];
		}[];
	};
}

export type SVGDetectionGroups = Exclude<ParsedMaskSvg['svg'], undefined>['g'];

type MaskPukekoLocation = {
	product_version_number?: number;
	resource_path?: string;
};

export class CollectionModelDrawableMask extends CollectionModelDrawable {
	constructor(
		collectionModelDrawableState: CollectionModelDrawableState,
		drawableId: string,
		pukekoLocation: MaskPukekoLocation
	) {
		super(collectionModelDrawableState, drawableId);
		makeObservable(this);

		this.pukekoLocation = { ...pukekoLocation };
	}

	/**
	 * @deprecated Write an async method to get this if it hasn't already been fetched, then return the value
	 */
	@observable parsedSvg: ParsedMaskSvg | null = null;

	svgStringInFlight: boolean = false;

	pukekoLocation: MaskPukekoLocation;

	override get isInsightMeasurement() {
		return true;
	}

	override get isMask(): boolean {
		return true;
	}

	override isApprovableInsightDrawable(): this is ApprovableInsightDrawable {
		return false;
	}

	override get isVisible(): boolean {
		return true;
	}

	override get x() {
		return (this.drawableState.capture?.resolution.width ?? 0) / 2;
	}

	override get y() {
		return (this.drawableState.capture?.resolution.height ?? 0) / 2;
	}

	public async fetchSvgMask(fetchingCallBack: () => Promise<string>) {
		// If the string already exists or is in flight, don't fetch it again
		if (this.parsedSvg) {
			return;
		}
		if (this.svgStringInFlight) {
			return;
		}

		this.svgStringInFlight = true;
		const svgString = await fetchingCallBack();
		this.parsedSvg = parser.parse(svgString);
		this.svgStringInFlight = false;
	}

	/**
	 * Check if the mask SVG has been loaded AND has visible detections.
	 *
	 * @returns The visible SVG detection groups, otherwise null.
	 */
	public getVisibleSvgDetectionGroups(enabledDetectionGroups: string[], log?: Log): SVGDetectionGroups | null {
		if (!this.parsedSvg) {
			log?.warn({ drawable: this.id }, 'getVisibleSvgDetectionGroups: SVG has not been loaded');
			return null;
		}
		if (!this.parsedSvg.svg) {
			log?.warn({ drawable: this.id }, 'getVisibleSvgDetectionGroups: Root of SVG is missing <svg> tag');
			return null;
		}
		if (!this.parsedSvg.svg.g?.length) {
			log?.warn({ drawable: this.id }, 'getVisibleSvgDetectionGroups: SVG contains no detection groups');
			return null;
		}
		// Check if any of the SVG detection groups are present in enabledDetectionGroups.
		if (!this.parsedSvg.svg.g.some((g) => enabledDetectionGroups.includes(g.id))) {
			log?.warn(
				{
					drawable: this.id,
					enabledDetectionGroups,
					maskDetectionGroups: this.parsedSvg.svg.g.map((g) => g.id),
				},
				'getVisibleSvgDetectionGroups: None of the detection groups in the SVG were listed in enabledDetectionGroups'
			);
			return null;
		}
		// Mask contains at least one visible detection group, which means the user can see it.
		return this.parsedSvg.svg.g;
	}

	static isMask(t: CollectionModelDrawable): t is CollectionModelDrawableMask {
		return t instanceof CollectionModelDrawableMask;
	}

	override clone(newParentDrawableState: CollectionModelDrawableState): CollectionModelDrawableMask {
		return new CollectionModelDrawableMask(newParentDrawableState, this.id, this.pukekoLocation);
	}

	override equals(other: CollectionModelDrawable): boolean {
		return (
			other instanceof CollectionModelDrawableMask &&
			this.pukekoLocation.product_version_number === other.pukekoLocation.product_version_number &&
			this.pukekoLocation.resource_path === other.pukekoLocation.resource_path &&
			this._equals(other)
		);
	}
}
